import React, { useContext } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Toolbar,
  Typography,
  Slide,
  useScrollTrigger,
  IconButton,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemIcon,
  Grid,
  useMediaQuery,
  Box,
  ListItemButton,
  Chip,
} from "@mui/material";
import {
  Menu,
  Home,
  Event,
  School,
  AccountCircle,
  Style,
  HelpOutline,
  Inventory,
  Work,
  Settings,
  Info,
  Security,
} from "@mui/icons-material";
import { ReactComponent as FireDepartmentLight } from "./../images/local_fire_department_light.svg";
import { withRouter } from "react-router-dom";

import { AuthContext } from "./Home";
import LoginButton from "./tools/LoginButton";
import AccountButton from "./tools/AccountButton";
import useDivision from "./hooks/useDivision";

function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({ target: window ? window() : undefined });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  window: PropTypes.func,
};

//Kombination aus Darwer und Appbar; bildet den Rahmen der App
function MainAppFrame(props) {
  const [division, setDivision] = useDivision("kfv");
  const [state, setState] = React.useState({
    open: false,
    type: division.toUpperCase(),
  });

  const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const routes = {
    "": "Neuigkeiten",
    fortbildungen: "Fortbildungen",
    kalender: "Kalender",
    feuerwehren: "Feuerwehren",
    login: "Anmelden",
    konto: "Konto",
    impressum: "Impressum",
    datenschutz: "Datenschutzerklärung",
    nutzer: "Nutzerverwaltung",
    partnercard: "Partnercard",
    verleih: "Verleih",
    jobs: "Jobs",
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setState({ ...state, open: open });
  };

  const handleListItemClick = (routeTo) => {
    props.history.push(routeTo);
    setState({ ...state, open: false });
  };

  if (props.history.location.pathname === "/setDivision") {
    let query = new URLSearchParams(props.history.location.search);
    let type = query.get("type").toLowerCase();
    if (type === "kfv" || type === "kjf") {
      setDivision(type);
    }
    props.history.push("/");
  }

  const Auth = useContext(AuthContext);

  window.addEventListener("divisionUpdate", () => {
    if (localStorage.getItem("division")) {
      let div = JSON.parse(localStorage.getItem("division")).toUpperCase();
      document.title = `${div}-LDS`;
      setState({ ...state, type: div });
    }
  });

  let version = "<unknown>";
  let appver = document.getElementsByTagName("meta").namedItem("app-version");
  if (appver) {
    version = appver.getAttribute("content");
  }

  return (
    <React.Fragment>
      <HideOnScroll {...props}>
        <AppBar enableColorOnDark>
          <Toolbar>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Grid container direction="row" alignItems="center">
                  <IconButton
                    edge="start"
                    onClick={toggleDrawer(true)}
                    color="inherit"
                  >
                    <Menu />
                  </IconButton>
                  <Typography variant="h6">
                    {`${state.type} LDS - ` +
                      routes[props.location.pathname.split("/")[1]]}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item>
                {Auth.isLoggedIn && Auth.user ? (
                  <AccountButton />
                ) : (
                  <LoginButton />
                )}
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <Toolbar />
      <SwipeableDrawer
        anchor={"left"}
        open={state.open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: "100%" }}
        >
          <Grid item>
            <Box sx={{ width: 280, pt: 1 }}>
              <List>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleListItemClick("/")}
                    sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                  >
                    <ListItemIcon>
                      <Home />
                    </ListItemIcon>
                    <ListItemText primary={"Neuigkeiten"} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleListItemClick("/kalender")}
                    sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                  >
                    <ListItemIcon>
                      <Event />
                    </ListItemIcon>
                    <ListItemText primary={"Kalender"} />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleListItemClick("/fortbildungen")}
                    sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                  >
                    <ListItemIcon>
                      <School />
                    </ListItemIcon>
                    <ListItemText primary={"Fortbildungen"} />
                  </ListItemButton>
                </ListItem>

                {Auth.isLoggedIn && Auth.user && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemClick("/partnercard")}
                      sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                    >
                      <ListItemIcon>
                        <Style />
                      </ListItemIcon>
                      <ListItemText primary={"Partnercard"} />
                    </ListItemButton>
                  </ListItem>
                )}

                {Auth.isLoggedIn && Auth.user && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemClick("/feuerwehren")}
                      sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                    >
                      <ListItemIcon>
                        <FireDepartmentLight
                          style={{
                            fill: prefersDarkMode ? "#C7C7C7" : "#757575",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={"Feuerwehren"} />
                    </ListItemButton>
                  </ListItem>
                )}

                {Auth.isLoggedIn && Auth.user && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemClick("/jobs")}
                      sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                    >
                      <ListItemIcon>
                        <Work />
                      </ListItemIcon>
                      <ListItemText primary={"Jobs"} />
                    </ListItemButton>
                  </ListItem>
                )}

                {Auth.isLoggedIn && Auth.user && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemClick("/verleih")}
                      sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                    >
                      <ListItemIcon>
                        <Inventory />
                      </ListItemIcon>
                      <ListItemText primary={"Verleih"} />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
              {Auth.isLoggedIn && Auth.user && Auth.user.role === "admin" && (
                <Box sx={{ mt: 1 }}>
                  <Divider sx={{ mx: 2, mb: 1 }}>
                    <Chip
                      label="Administration"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  </Divider>

                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemClick("/nutzer")}
                      sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                    >
                      <ListItemIcon>
                        <AccountCircle />
                      </ListItemIcon>
                      <ListItemText primary={"Nutzerverwaltung"} />
                    </ListItemButton>
                  </ListItem>

                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleListItemClick("/verleih/verwaltung")}
                      sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                    >
                      <ListItemIcon>
                        <Settings />
                      </ListItemIcon>
                      <ListItemText primary={"Verleihverwaltung"} />
                    </ListItemButton>
                  </ListItem>
                </Box>
              )}
              <Divider sx={{ mx: 2, my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleListItemClick("/impressum")}
                  sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                >
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText primary={"Impressum"} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleListItemClick("/datenschutz")}
                  sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                >
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText primary={"Datenschutzerklärung"} />
                </ListItemButton>
              </ListItem>
            </Box>
          </Grid>
          <Grid item>
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                {`Version: ${version}`}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setState({ ...state, open: false });
                  props.showOnboarding();
                }}
                sx={{ color: "text.secondary" }}
              >
                <HelpOutline />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </SwipeableDrawer>
    </React.Fragment>
  );
}

export default withRouter(MainAppFrame);
